import { z } from "zod";

// TES-591 — doctor-signed publishing. The individual
// medical notices a patient reads are published by a DOCTOR through
// "Godkänn och publicera"; every publication is kept as an immutable version
// so a later change never erases what she read. The kinds:
//   cp09_decision      — treatment approved / not approved (CP-09)
//   cp22_dose          — a published dose change (CP-22)
//   cp33_review        — the signed monthly review feedback (CP-33)
//   consult_next_step  — the consult's next step (CP-06 routing)
//   goal_weight_adjusted — the doctor corrected the patient's goal weight
//   prescription_corrected — a mistyped prescription put right (Ö-228)
//   prescription_confirmed — TES-1070: the ordination frozen by the final
//                            transaction that confirms the prescription and
//                            starts the treatment
//   medical_question_answer — TES-1120: the doctor's answer to a patient's
//                            non-acute medical question (a MedicalReviewCase
//                            from the coach or Ella Support). Plain text
//                            written for her, read in Din vård.
//   doctor_notice       — TES-1147: a notice the doctor sends on his own
//                            initiative (no question behind it), e.g. a
//                            follow-up that needs her to do something. Same
//                            shape as medical_question_answer: plain text,
//                            read in Din vård, optionally with a task. It is
//                            how the doctor reaches a patient now that the
//                            doctor chat is no contact path (Emelie
//                            2026-10-07: "Om Kirill behöver svar skapas en
//                            strukturerad uppgift").
//   meeting_info        — TES-1393 (09C 2026-10-13, Ö-561): "Om ditt
//                            vårdmöte", practical word about a care meeting
//                            (a technical fault, a cancelled or moved
//                            meeting), with at most one CTA.
//   meeting_proposal    — TES-1393: "Förslag om extra vårdmöte", the doctor
//                            recommends another meeting; it opens a
//                            traceable booking task and never marks a
//                            meeting booked on its own.
//   The two meeting kinds are read under "Din vård → Vårdmöten", never as care
//   notes. A build before CLIENT_CONTRACT_CHANGES.meetingNotices is served
//   them as `doctor_notice`, exactly as it reads them today.
//
// TES-863 (Ö-229) — `goal_weight_adjusted` is the odd member and deliberately
// so. The goal weight is HERS: she sets it in the app, and Emelie's answer is
// that Kirill may correct it, with the condition that
// "patienten får veta att läkaren har justerat det".
// A notice is what makes that true and keeps the previous value: Ö-228's rule
// for a prescription — "det ursprungliga värdet får aldrig skrivas över" —
// applies just as well to a goal somebody else changed.
//
// TES-863 (Ö-228) — `prescription_corrected` is its own kind and not a second
// `cp22_dose`, for the reason Emelie gives:
// "en dosändring är ett kliniskt beslut med eget datum, en rättelse är ett tangentnedslag som aldrig var ett beslut".
// Filing a keystroke under the kind that means "the doctor decided to change
// her dose" would make the clinical record claim a decision nobody took. The
// versioning is what carries her other rule —
// "det ursprungliga värdet får aldrig skrivas över i auditspåret" —
// since a new version never touches the one before it.
export const MEDICAL_NOTICE_KINDS = [
  "cp09_decision",
  "cp22_dose",
  "cp33_review",
  "consult_next_step",
  "goal_weight_adjusted",
  "prescription_corrected",
  "prescription_confirmed",
  "medical_question_answer",
  "doctor_notice",
  "meeting_info",
  "meeting_proposal",
] as const;
export const medicalNoticeKindSchema = z.enum(MEDICAL_NOTICE_KINDS);
export type MedicalNoticeKind = z.infer<typeof medicalNoticeKindSchema>;

// TES-1393 — the doctor's messages about care meetings (see above).
export const MEETING_NOTICE_KINDS = [
  "meeting_info",
  "meeting_proposal",
] as const;
export type MeetingNoticeKind = (typeof MEETING_NOTICE_KINDS)[number];
export function isMeetingNoticeKind(kind: string): kind is MeetingNoticeKind {
  return (MEETING_NOTICE_KINDS as readonly string[]).includes(kind);
}

export const medicalNoticeVersionSchema = z.object({
  id: z.string().uuid(),
  kind: medicalNoticeKindSchema,
  subjectType: z.string(),
  subjectId: z.string(),
  version: z.number().int().positive(),
  body: z.string(),
  publishedByDoctorId: z.string(),
  publishedAt: z.string(),
});
export type MedicalNoticeVersion = z.infer<typeof medicalNoticeVersionSchema>;

export const medicalNoticeHistorySchema = z.object({
  versions: z.array(medicalNoticeVersionSchema),
});
export type MedicalNoticeHistory = z.infer<typeof medicalNoticeHistorySchema>;
